import { useState } from "react";
import { Button } from "react-bootstrap";
import ConditionModal from "./ConditionModal";
import ConditionPreview from "./ConditionPreview";
import { Plus } from "react-bootstrap-icons";
import { normalizeConditionForSave } from "../utils/normalizeCondition";

import {
  humanizeName,
  CALC_KIND_BADGE_COLORS
} from "../utils/rateConditions";

export default function RateConditionsStep({
  catalogConditions = [],
  selectedConditions = [],
  rateConcepts = [],
  places = [],
  onChange,
}) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const addConditionFromCatalog = (condition) => {
    if (selectedConditions.some(c => c.name === condition.name)) return;

    const cloned = {
      ...condition,
      rate_id: "__LOCAL__",
    };

    onChange([...selectedConditions, cloned]);
  };

  const addNewCondition = (condition) => {
    onChange([...selectedConditions, condition]);
  };

  const updateCondition = (updated) => {
    onChange(
      selectedConditions.map((c) =>
        c.id === updated.id ? updated : c
      )
    );
  };

  const removeCondition = (id) => {
    onChange(selectedConditions.filter((c) => c.id !== id));
  };

  return (
    <section className="card">
      <h2>Paso 3 — Condiciones</h2>

      <p className="wizard-guide">
        Seleccione o cree las condiciones que aplican a esta tarifa.
        Estas condiciones se podrán aplicar a las reglas del paso 4.
      </p>

      <div className="concepts-grid">

        {/* ===============================
            PANEL IZQUIERDO — CATÁLOGO
          =============================== */}
        <div className="panel">
          <h3>Catálogo de condiciones</h3>

          {catalogConditions.length === 0 && (
            <p className="muted">No hay condiciones globales.</p>
          )}

          <div className="condition-list">
            <div className="table-scroll">
              <table className="wizard-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th />
                  </tr>
                </thead>
                  <tbody>
                    {catalogConditions.map((c) => (
                      <tr key={c.id}>
                        <td>{humanizeName(c.name)}</td>
                        <td>
                          {(() => {
                            const kindKey = String(c.type ?? "").trim().toUpperCase();
                            return (
                              <span className={`badge ${CALC_KIND_BADGE_COLORS[kindKey]}`}>
                                {badgeByType(c.type)}
                              </span>
                            );
                          })()}
                        </td>
                        <td><ConditionPreview condition={c} places={places} /></td>
                        <td>
                          <button
                            className="btn-icon"
                            onClick={() => addConditionFromCatalog(c)}
                          >
                            <Plus />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                variant="outline-success"
                onClick={() => {
                  setEditing(null);
                  setShowModal(true);
                }}
              >
                Nueva condición <Plus />
              </Button>
            </div>

          
        </div>

        {/* ===============================
            PANEL DERECHO — SELECCIONADAS
          =============================== */}
        <div className="panel">
          <h3>Condiciones de esta tarifa</h3>

          {selectedConditions.length === 0 && (
            <p className="muted">
              Aún no ha definido condiciones para esta tarifa.
            </p>
          )}

          <div className="condition-list-scroll">
            <div className="condition-list">
              {selectedConditions.map((c) => (
                <div key={c.id} className="condition-card">
                  <div className="condition-header">
                    <strong>{humanizeName(c.name)}</strong>

                    <span
                      className={`badge ${CALC_KIND_BADGE_COLORS[c.type]}`}
                    >
                      {badgeByType(c.type)}
                    </span>
                  </div>

                  <div className="concept-summary">
                    <ConditionPreview
                      condition={c}
                      places={places}
                    />
                  </div>

                  <div className="actions">
                    <Button
                      size="sm"
                      style={{marginRight: '2%'}}
                      variant="outline-info"
                      onClick={() => {
                        setEditing(c);
                        setShowModal(true);
                      }}
                    >
                      Editar
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeCondition(c.id)}
                    >
                      Quitar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConditionModal
        show={showModal}
        initialValue={editing}
        places={places}
        rateConcepts={rateConcepts}
        onClose={() => setShowModal(false)}
        onSave={(cond) => {
          const normalized = normalizeConditionForSave({
            ...cond,
            id: cond.id ?? null,       // temporal frontend
            rate_id: "__LOCAL__",  // aún no persistida
          });

          editing
            ? updateCondition(normalized)
            : addNewCondition(normalized);

          setShowModal(false);
        }}
      />

    </section>
  );
}

function badgeByType(type) {
  switch (type) {
    case "TIME_RANGE": return "Horario";
    case "DAY_KIND": return "Día";
    case "ROUTE_ORIGIN": return "Origen";
    case "ROUTE_DESTINATION": return "Destino";
    case "ROUTE_PASSES_THROUGH": return "Zona";
    case "EXEMPTION": return "Excepción";
    default: return type;
  }
}
